import { motion } from 'framer-motion';
import {
  Coins,
  ExternalLink,
  HelpCircle,
  Info,
  LogIn,
  LogOut,
  Lock,
  Mail,
  Palette,
  Shield,
  Sparkles,
  Star,
  Trophy,
  User as UserIcon,
} from 'lucide-react';
import { useState } from 'react';
import {
  Button,
  Card,
  ListRow,
  ProBadge,
  SectionHeader,
  SegmentedControl,
  useToast,
} from '../../components';
import { useActions, useAppData } from '../../data';
import { formatCurrency } from '../../lib/currency';
import { formatPayDate } from '../../lib/date';
import { getNextPayDate } from '../../lib/payPeriod';
import type { ThemePreference } from '../../lib/theme';
import { springGentle } from '../../tokens';
import { AchievementsSheet } from '../../achievements';
import { useProContext } from '../../pro';
import { useRatePrompt } from '../../rating';
import { useAuth } from '../../supabase';
import { useWalkthrough } from '../../walkthrough';
import { AccentPicker } from './AccentPicker';
import { ManualAccountsSection } from './ManualAccountsSection';
import { SharedBudgetsSection } from './SharedBudgetsSection';
import { CategoriesSection } from './CategoriesSection';
import { QuickPresetsSheet } from './QuickPresetsSheet';
import { BulkPasteSheet } from './BulkPasteSheet';
import { ManageReceiptsSheet } from './ManageReceiptsSheet';
import { AchievementsScreen } from './Achievements';
import { DataSection } from './DataSection';
import {
  EditBudgetModelSheet,
  EditHourlyRateSheet,
  EditNameSheet,
  EditPayCycleSheet,
  EditRolloverSheet,
  EditWorkScheduleSheet,
} from './ProfileSheets';
import { ToolsSection } from './ToolsSection';

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05 } },
};

const themeOptions: { value: ThemePreference; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

/** 1px hairline divider — uses surface-secondary so it adapts to dark mode. */
function Divider() {
  return <div className="h-px bg-surface-secondary mx-card-pad" />;
}

type ProfileSheet =
  | 'name'
  | 'hourly'
  | 'cycle'
  | 'model'
  | 'rollover'
  | 'workSchedule'
  | null;

export function SettingsScreen() {
  const data = useAppData();
  const actions = useActions();
  const toast = useToast();
  const walkthrough = useWalkthrough();
  const ratePrompt = useRatePrompt();
  const pro = useProContext();
  const auth = useAuth();

  const [profileSheet, setProfileSheet] = useState<ProfileSheet>(null);
  const [achievementsOpen, setAchievementsOpen] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [bulkPasteOpen, setBulkPasteOpen] = useState(false);
  const [receiptsOpen, setReceiptsOpen] = useState(false);
  const [achievementsScreenOpen, setAchievementsScreenOpen] = useState(false);

  const unlockedCount = data.achievements.filter((a) => !!a.unlockedAt).length;

  const cycleLabel = formatCycle(data.profile.payCycle);
  const modelLabel = formatModel(data.profile.budgetModel);
  const nextPay = formatPayDate(getNextPayDate(data.profile));
  const isPYF = data.profile.budgetModel === 'pyf';

  return (
    <motion.main
      initial="hidden"
      animate="show"
      variants={stagger}
      className="mx-auto max-w-content px-screen-x pt-section-gap"
    >
      <motion.header
        variants={fadeUp}
        transition={springGentle}
        className="mb-section-gap"
      >
        <h1 className="text-section">Settings</h1>
      </motion.header>

      {/* Profile */}
      <motion.div variants={fadeUp} transition={springGentle}>
        <SectionHeader title="Profile" />
        <Card flush elevation="sm" className="mb-section-gap py-1">
          <ListRow
            title="Name"
            value={data.profile.name || '—'}
            onClick={() => setProfileSheet('name')}
          />
          <Divider />
          <ListRow
            title="Pay cycle"
            value={cycleLabel}
            onClick={() => setProfileSheet('cycle')}
          />
          <Divider />
          <ListRow
            title="Next payday"
            value={nextPay}
            onClick={() => setProfileSheet('cycle')}
          />
          <Divider />
          <ListRow
            title="Budget model"
            value={modelLabel}
            onClick={() => setProfileSheet('model')}
          />
          <Divider />
          <ListRow
            title="Rollover balance"
            value={
              (data.profile.rollover ?? 'continuous') === 'continuous'
                ? 'Continuous'
                : 'Fresh start'
            }
            onClick={() => setProfileSheet('rollover')}
          />
          {isPYF ? (
            <>
              <Divider />
              <ListRow
                title="Hourly rate"
                value={
                  data.profile.hourlyRate
                    ? formatCurrency(data.profile.hourlyRate, {
                        currency: data.profile.currency,
                      })
                    : '—'
                }
                onClick={() => setProfileSheet('hourly')}
              />
            </>
          ) : null}
          <Divider />
          <ListRow
            title="Work schedule"
            value={`${data.profile.workDaysPerWeek ?? 5} days/week`}
            onClick={() => setProfileSheet('workSchedule')}
          />
        </Card>
      </motion.div>

      {/* Manual accounts (visibility layer; not used by budget math) */}
      <motion.div variants={fadeUp} transition={springGentle}>
        <ManualAccountsSection />
      </motion.div>

      {/* Shared budgets — Pro stub */}
      <motion.div variants={fadeUp} transition={springGentle}>
        <SharedBudgetsSection />
      </motion.div>

      {/* Pro */}
      <motion.div variants={fadeUp} transition={springGentle}>
        <SectionHeader title="Margin Pro" />
        <Card elevation="sm" className="mb-section-gap">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-button bg-warning/15 inline-flex items-center justify-center text-warning shrink-0">
              <Sparkles size={20} strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="text-[15px] font-semibold text-text-primary">
                  {pro.isPro
                    ? data.proPurchased
                      ? 'Pro is active'
                      : `Trial active · ${pro.trialDaysLeft} day${pro.trialDaysLeft === 1 ? '' : 's'} left`
                    : 'Forecast, net worth, advanced insights'}
                </div>
                <ProBadge />
              </div>
              <p className="mt-1 text-[13px] text-text-secondary leading-snug">
                {pro.isPro
                  ? data.proPurchased
                    ? 'Thanks for supporting Margin. Manage your subscription in your platform’s settings.'
                    : 'You have full access to every Pro feature.'
                  : 'Cash flow forecast, net worth, advanced insights, and more. Free 7-day trial.'}
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            className="font-semibold"
            onClick={pro.openPaywall}
          >
            {pro.isPro
              ? data.proPurchased
                ? 'Manage subscription'
                : 'See Pro features'
              : 'Start 7-day free trial'}
          </Button>
        </Card>
      </motion.div>

      {/* Sign-in (Supabase) — only shown when env vars are set. Free
          users never need an account; this row appears once Pro
          features need cloud state (couples sync). */}
      {auth.configured ? (
        <motion.div variants={fadeUp} transition={springGentle}>
          <SectionHeader title="Account" />
          <Card flush elevation="sm" className="mb-section-gap py-1">
            {auth.isAuthenticated ? (
              <>
                <ListRow
                  icon={<UserIcon size={18} strokeWidth={1.5} />}
                  title="Signed in"
                  value={auth.email ?? '—'}
                  accessory="none"
                />
                <Divider />
                <ListRow
                  icon={<LogOut size={18} strokeWidth={1.5} />}
                  title="Sign out"
                  destructive
                  onClick={async () => {
                    await auth.signOut();
                    toast.show({ message: 'Signed out', kind: 'info' });
                  }}
                />
              </>
            ) : (
              <ListRow
                icon={<LogIn size={18} strokeWidth={1.5} />}
                title="Sign in to sync"
                subtitle="Magic link, no password"
                accessory="chevron"
                onClick={auth.openSignIn}
              />
            )}
          </Card>
        </motion.div>
      ) : null}

      {/* Appearance */}
      <motion.div variants={fadeUp} transition={springGentle}>
        <SectionHeader title="Appearance" />
        <Card elevation="sm" className="mb-section-gap">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-button bg-surface-secondary inline-flex items-center justify-center text-text-secondary shrink-0">
              <Palette size={18} strokeWidth={1.5} />
            </div>
            <div className="text-[14px] font-medium text-text-primary">
              Theme
            </div>
          </div>
          <SegmentedControl
            options={themeOptions}
            value={data.theme}
            onChange={actions.setTheme}
          />

          <div className="mt-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[13px] font-medium text-text-primary">
                Accent color
              </span>
              {!data.rated ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-text-tertiary">
                  <Lock size={10} strokeWidth={2} />
                  Stellar locked
                </span>
              ) : null}
            </div>
            <AccentPicker
              value={data.accentColor}
              onChange={actions.setAccentColor}
              stellarUnlocked={data.rated}
              onStellarLockedTap={() =>
                toast.show({
                  message: 'Rate Margin to unlock the Stellar theme',
                  kind: 'info',
                })
              }
            />
          </div>
        </Card>
      </motion.div>

      {/* Categories */}
      <motion.div variants={fadeUp} transition={springGentle}>
        <CategoriesSection />
      </motion.div>

      {/* Quick Add */}
      <motion.div variants={fadeUp} transition={springGentle}>
        <SectionHeader title="Quick Add" />
        <Card flush elevation="sm" className="mb-section-gap py-1">
          <ListRow
            title="Quick Add Presets"
            subtitle="Up to 8 one-tap shortcuts"
            accessory="chevron"
            onClick={() => setPresetsOpen(true)}
          />
          <Divider />
          <ListRow
            title="Bulk Add Transactions"
            subtitle="Paste a list, one per line"
            accessory="chevron"
            onClick={() => setBulkPasteOpen(true)}
          />
        </Card>
      </motion.div>

      {/* Tools */}
      <motion.div variants={fadeUp} transition={springGentle}>
        <ToolsSection />
      </motion.div>

      {/* Data */}
      <motion.div variants={fadeUp} transition={springGentle}>
        <DataSection />
        <Card flush elevation="sm" className="mb-section-gap py-1">
          <ListRow
            title="Manage Receipts"
            subtitle="View, remove, or clear saved receipts"
            accessory="chevron"
            onClick={() => setReceiptsOpen(true)}
          />
        </Card>
      </motion.div>

      {/* Help */}
      <motion.div variants={fadeUp} transition={springGentle}>
        <SectionHeader title="Help" />
        <Card flush elevation="sm" className="mb-section-gap py-1">
          <ListRow
            icon={<Trophy size={18} strokeWidth={1.5} />}
            title="Achievements"
            // Wired to the full Achievements screen rather than the
            // legacy summary sheet so taps land on the new grid.
            subtitle={`${unlockedCount} of ${data.achievements.length} unlocked`}
            accessory="chevron"
            onClick={() => setAchievementsScreenOpen(true)}
          />
          <Divider />
          <ListRow
            icon={<HelpCircle size={18} strokeWidth={1.5} />}
            title="App tour"
            subtitle="Replay the walkthrough"
            accessory="chevron"
            onClick={() => walkthrough.start()}
          />
          <Divider />
          <ListRow
            icon={<Star size={18} strokeWidth={1.5} />}
            title="Rate Margin"
            subtitle={
              data.rated
                ? 'Thanks! Stellar theme unlocked'
                : 'Unlock the Stellar theme'
            }
            accessory="chevron"
            onClick={() => ratePrompt.show()}
          />
          <Divider />
          <ListRow
            icon={<Mail size={18} strokeWidth={1.5} />}
            title="Contact support"
            subtitle="support@marginbudget.com"
            accessory={
              <ExternalLink size={16} className="text-text-tertiary" />
            }
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.location.href =
                  'mailto:support@marginbudget.com?subject=Margin%20support';
              }
            }}
          />
        </Card>
      </motion.div>

      {/* About */}
      <motion.div variants={fadeUp} transition={springGentle}>
        <SectionHeader title="About" />
        <Card flush elevation="sm" className="mb-section-gap py-1">
          <ListRow
            icon={<Info size={18} strokeWidth={1.5} />}
            title="Margin"
            value="v0.1.0"
            accessory="none"
          />
          <Divider />
          <ListRow
            icon={<Coins size={18} strokeWidth={1.5} />}
            title="Cook Media Systems"
            subtitle="Designed in Tennessee. Made in the USA!"
            accessory="none"
          />
          <Divider />
          <ListRow
            icon={<Shield size={18} strokeWidth={1.5} />}
            title="Privacy policy"
            accessory={
              <ExternalLink size={16} className="text-text-tertiary" />
            }
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.open('https://marginbudget.com/privacy', '_blank');
              }
            }}
          />
          <Divider />
          <ListRow
            icon={<Shield size={18} strokeWidth={1.5} />}
            title="Terms of service"
            accessory={
              <ExternalLink size={16} className="text-text-tertiary" />
            }
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.open('https://marginbudget.com/terms', '_blank');
              }
            }}
          />
          <div className="px-card-pad py-2 text-[11px] text-text-tertiary font-mono tabular-nums text-center">
            Build: {__BUILD_ID__}
          </div>
        </Card>
      </motion.div>

      <p className="mt-4 pb-card-gap text-[13px] font-medium uppercase tracking-[0.05em] text-[#8E8E93] text-center">
        Margin by Cook Media Systems. Designed in Tennessee. Made in the USA!
      </p>

      {/* Profile sheets */}
      <EditNameSheet
        open={profileSheet === 'name'}
        current={data.profile.name}
        onClose={() => setProfileSheet(null)}
        onSave={(name) => {
          actions.updateProfile({ name });
          toast.show({ message: 'Saved', kind: 'success' });
        }}
      />
      <EditHourlyRateSheet
        open={profileSheet === 'hourly'}
        current={data.profile.hourlyRate ?? 0}
        onClose={() => setProfileSheet(null)}
        onSave={(hourlyRate) => {
          actions.updateProfile({ hourlyRate });
          toast.show({ message: 'Rate updated', kind: 'success' });
        }}
      />
      <EditPayCycleSheet
        open={profileSheet === 'cycle'}
        profile={data.profile}
        onClose={() => setProfileSheet(null)}
        onSave={(patch) => {
          actions.updateProfile(patch);
          toast.show({ message: 'Pay cycle updated', kind: 'success' });
        }}
      />
      <EditBudgetModelSheet
        open={profileSheet === 'model'}
        current={data.profile.budgetModel}
        onClose={() => setProfileSheet(null)}
        onSave={(budgetModel) => {
          actions.updateProfile({ budgetModel });
          toast.show({
            message: `Switched to ${formatModel(budgetModel)}`,
            kind: 'success',
          });
        }}
      />
      <EditRolloverSheet
        open={profileSheet === 'rollover'}
        current={data.profile.rollover ?? 'continuous'}
        onClose={() => setProfileSheet(null)}
        onSave={(rollover) => {
          actions.updateProfile({ rollover });
          toast.show({
            message:
              rollover === 'continuous'
                ? 'Rollover: continuous'
                : 'Rollover: fresh start',
            kind: 'success',
          });
        }}
      />
      <EditWorkScheduleSheet
        open={profileSheet === 'workSchedule'}
        current={data.profile.workDaysPerWeek ?? 5}
        onClose={() => setProfileSheet(null)}
        onSave={(workDaysPerWeek) => {
          actions.updateProfile({ workDaysPerWeek });
          toast.show({
            message: `Work schedule: ${workDaysPerWeek} days/week`,
            kind: 'success',
          });
        }}
      />

      <AchievementsSheet
        open={achievementsOpen}
        onClose={() => setAchievementsOpen(false)}
      />

      <QuickPresetsSheet
        open={presetsOpen}
        onClose={() => setPresetsOpen(false)}
      />

      <BulkPasteSheet
        open={bulkPasteOpen}
        onClose={() => setBulkPasteOpen(false)}
      />

      <ManageReceiptsSheet
        open={receiptsOpen}
        onClose={() => setReceiptsOpen(false)}
      />

      <AchievementsScreen
        open={achievementsScreenOpen}
        onClose={() => setAchievementsScreenOpen(false)}
      />
    </motion.main>
  );
}

function formatCycle(
  cycle: 'weekly' | 'biweekly' | 'semimonthly' | 'monthly',
): string {
  switch (cycle) {
    case 'weekly':
      return 'Weekly';
    case 'biweekly':
      return 'Biweekly';
    case 'semimonthly':
      return 'Semi-monthly';
    case 'monthly':
      return 'Monthly';
  }
}

function formatModel(model: string): string {
  switch (model) {
    case 'daily':
      return 'Daily Allowance';
    case '503020':
      return '50 / 30 / 20';
    case 'pyf':
      return 'Pay Yourself First';
    case 'zero':
      return 'Zero-Based';
    case 'conscious':
      return 'Conscious Spending';
    default:
      return model;
  }
}
